import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { fetchAuthSession, getCurrentUser } from "@aws-amplify/auth";
import { Loader2 } from "lucide-react";

type UserRole = "admin" | "researcher";

interface AuthContextValue {
  role: UserRole;
  username: string;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<AuthContextValue | null>(null);

  useEffect(() => {
    async function resolve() {
      try {
        const [session, user] = await Promise.all([fetchAuthSession(), getCurrentUser()]);
        const groups = (session.tokens?.accessToken?.payload?.["cognito:groups"] as string[]) ?? [];
        const role: UserRole = groups.includes("admin") ? "admin" : "researcher";
        setValue({ role, username: user.username, isAdmin: role === "admin" });
      } catch {
        setValue({ role: "researcher", username: "", isAdmin: false });
      }
    }
    resolve();
  }, []);

  if (!value) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
